import { SegmentedControl } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { getSearchProfiles, type SearchProfile } from '../lib/api.js';

const TYP_LABEL: Record<string, string> = {
  wohnung: 'Wohnung',
  haus: 'Haus',
  grundstueck: 'Grundstück',
};

/** Kurz-Label nach Objekt-Typ (Wohnung/Haus) statt Profilname („Kauf Wohnung"). */
function shortLabel(p: SearchProfile): string {
  return TYP_LABEL[p.objektTyp] ?? p.name;
}

/**
 * Filter über die benutzerdefinierten Such-Profile des aktuellen Kontos. Ersetzt den
 * alten hartkodierten „Wohnung/Büro"-Filter — zeigt genau die Profile, die der User hat
 * (z.B. Miete Wohnung / Kauf Wohnung / Kauf Haus). Bei ≤1 Profil wird nichts angezeigt.
 */
export function SearchProfileFilter({
  value,
  onChange,
}: {
  value: number | undefined;
  onChange: (id: number | undefined) => void;
}): JSX.Element | null {
  const { data } = useQuery({ queryKey: ['search-profiles'], queryFn: getSearchProfiles });
  const profiles = (data?.profiles ?? []).filter((p) => p.active);
  if (profiles.length <= 1) return null;
  // Bei Label-Kollision (z.B. Miete + Kauf Wohnung → beide „Wohnung") den Deal-Typ voranstellen.
  const labelOf = (p: SearchProfile): string => {
    const base = shortLabel(p);
    const clash = profiles.some((o) => o.id !== p.id && shortLabel(o) === base);
    return clash ? `${p.dealType === 'miete' ? 'Miete' : 'Kauf'} ${base}` : base;
  };
  return (
    <SegmentedControl
      value={value ? String(value) : 'all'}
      onChange={(v) => onChange(v === 'all' ? undefined : Number(v))}
      data={[
        { label: 'Alle', value: 'all' },
        ...profiles.map((p) => ({ label: labelOf(p), value: String(p.id) })),
      ]}
      size="sm"
      radius="md"
    />
  );
}
